import { useNavigate } from 'react-router-dom';
import { Trophy, Upload, DollarSign, Megaphone, Music, ArrowRight, Star, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

export default function Contest() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-purple-900 via-pink-800 to-orange-700 py-20 px-4">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgY3g9IjMwIiBjeT0iMzAiIHI9IjMiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4xKSIvPjwvZz48L3N2Zz4=')] opacity-40"></div>
        
        <div className="container mx-auto text-center relative z-10">
          <div className="flex justify-center gap-2 mb-6">
            <Trophy className="w-12 h-12 text-yellow-400 animate-bounce" />
            <Sparkles className="w-8 h-8 text-yellow-300 animate-pulse" />
            <Trophy className="w-12 h-12 text-yellow-400 animate-bounce" />
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 tracking-tight">
            SOUNDOPE'S HAVING A
            <span className="block text-yellow-400 mt-2">CONTEST!!</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl mx-auto">
            Upload your track for a chance to be featured in our next ad campaign and win <span className="text-green-400 font-bold">$100</span>!
          </p>

          <Button 
            size="lg" 
            onClick={() => navigate('/Upload')}
            className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-lg px-8 py-6 rounded-full shadow-2xl hover:shadow-yellow-500/50 transition-all hover:scale-105"
          >
            <Upload className="w-5 h-5 mr-2" />
            Enter the Contest
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>

      {/* How It Works */}
      <div className="container mx-auto py-16 px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
          How to <span className="text-primary">Enter</span>
        </h2>

        <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          <Card className="bg-card/50 border-primary/20 hover:border-primary/50 transition-colors">
            <CardContent className="pt-8 text-center">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Upload className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-2">1. Upload Your Track</h3>
              <p className="text-muted-foreground">
                Submit your best track through our upload page. All genres welcome!
              </p>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-primary/20 hover:border-primary/50 transition-colors">
            <CardContent className="pt-8 text-center">
              <div className="w-16 h-16 bg-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Music className="w-8 h-8 text-pink-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">2. Get Discovered</h3>
              <p className="text-muted-foreground">
                Your track will be reviewed by our community and team for the contest.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-primary/20 hover:border-primary/50 transition-colors">
            <CardContent className="pt-8 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">3. Win Big!</h3>
              <p className="text-muted-foreground">
                The winner gets $100 and their track featured in our ad campaign!
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Prizes Section */}
      <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 py-16 px-4">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            <Star className="inline w-8 h-8 text-yellow-400 mr-2" />
            Grand Prize
            <Star className="inline w-8 h-8 text-yellow-400 ml-2" />
          </h2>

          <div className="max-w-2xl mx-auto">
            <Card className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border-yellow-500/50 overflow-hidden">
              <CardContent className="pt-8">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-2xl">
                    <DollarSign className="w-12 h-12 text-black" />
                  </div>
                  
                  <div className="text-center md:text-left flex-1">
                    <h3 className="text-4xl font-bold text-yellow-400 mb-2">$100 Cash Prize</h3>
                    <p className="text-lg text-foreground/80 mb-4">
                      Plus your track will be featured in Soundope's next official ad campaign!
                    </p>
                    <div className="flex items-center gap-2 justify-center md:justify-start">
                      <Megaphone className="w-5 h-5 text-primary" />
                      <span className="text-muted-foreground">Exposure to thousands of music lovers</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Rules Section */}
      <div className="container mx-auto py-16 px-4">
        <h2 className="text-3xl font-bold text-center mb-8">Contest Rules</h2>
        
        <div className="max-w-2xl mx-auto space-y-4 text-muted-foreground">
          <p>• Must be an original track created by you</p>
          <p>• One entry per artist</p>
          <p>• Track must be uploaded through Soundope's platform</p>
          <p>• Winner will be selected based on community engagement and quality</p>
          <p>• Contest ends when we find our winner!</p>
          <p>• Winner will be contacted via email</p>
        </div>

        <div className="text-center mt-12">
          <Button 
            size="lg" 
            onClick={() => navigate('/Upload')}
            className="bg-primary hover:bg-primary/90 font-bold text-lg px-8 py-6 rounded-full"
          >
            <Upload className="w-5 h-5 mr-2" />
            Upload Your Track Now
          </Button>
        </div>
      </div>
    </div>
  );
}
